import type { Session } from "@supabase/supabase-js"
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { BARCA_WORKSPACE_DOMAIN, isBarcaWorkspaceEmail } from "@/lib/auth"
import { env } from "@/lib/env"
import { supabase } from "@/lib/supabase"
import type { AppUser, Role } from "@/types/domain"

interface AuthContextValue {
  user: AppUser | null
  loading: boolean
  isDemo: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)
const demoUser: AppUser = { id: "demo-admin", name: "Marina Rocha", email: "marina@barca.dev", role: "ADMIN" }

async function resolveUser(session: Session | null, recordAccess = false): Promise<AppUser | null> {
  if (!session?.user || !supabase) return null
  if (!isBarcaWorkspaceEmail(session.user.email)) {
    window.sessionStorage.setItem("barca-auth-error", `Use uma conta Google Workspace @${BARCA_WORKSPACE_DOMAIN}.`)
    await supabase.auth.signOut()
    return null
  }
  const { data } = await supabase
    .from("profiles")
    .select("display_name, role")
    .eq("id", session.user.id)
    .maybeSingle()
  if (recordAccess && data) {
    const { error } = await supabase.rpc("record_farol_access")
    if (error) console.error("Não foi possível registrar o acesso ao FAROL.", error.code)
  }
  return {
    id: session.user.id,
    email: session.user.email ?? "",
    name: data?.display_name ?? session.user.email?.split("@")[0] ?? "Usuário",
    role: (data?.role as Role | undefined) ?? "VIEWER",
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(env.demoMode ? demoUser : null)
  const [loading, setLoading] = useState(!env.demoMode)

  useEffect(() => {
    if (!supabase) return
    let active = true
    void supabase.auth.getSession().then(async ({ data }) => {
      const current = await resolveUser(data.session, true)
      if (active) { setUser(current); setLoading(false) }
    })
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      window.setTimeout(() => {
        void resolveUser(session, event === "SIGNED_IN").then((current) => { if (active) setUser(current) })
      }, 0)
    })
    return () => { active = false; listener.subscription.unsubscribe() }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    isDemo: env.demoMode,
    signInWithGoogle: async () => {
      if (!supabase) return
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${env.appUrl.replace(/\/$/, "")}/login`,
          queryParams: { hd: BARCA_WORKSPACE_DOMAIN, prompt: "select_account" },
        },
      })
      if (error) throw error
    },
    signOut: async () => {
      if (env.demoMode) return
      await supabase?.auth.signOut()
      setUser(null)
    },
  }), [loading, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used within AuthProvider")
  return context
}
